import { useQueryClient } from "@tanstack/react-query";
import { Autocomplete, useFilter } from "react-aria-components";
import { Controller } from "react-hook-form";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Input } from "@/components/field";
import { ChevronDownIcon } from "@/components/icons.tsx";
import { SearchField } from "@/components/search-field";
import {
  Select,
  SelectListBox,
  SelectListItem,
  SelectListItemDescription,
  SelectListItemLabel,
  SelectPopover,
} from "@/components/select";
import { Text } from "@/components/text";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetCreatorOptions } from "@/features/request/edit/hooks/useGetCreatorOptions";
import { requestKeys } from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";

export function SelectCreator() {
  const methods = useEditRequestFormContext();
  const { data: request } = useGetRequest();
  const { data: creators } = useGetCreatorOptions(request?.id);
  const { updateField } = useFieldUpdate();
  const queryClient = useQueryClient();
  const { contains } = useFilter({ sensitivity: "base" });

  const handleUpdate = (
    creatorId: number,
    onChange: (...event: unknown[]) => void,
  ) => {
    onChange(creatorId);
    void updateField("creatorId", creatorId, () => {
      if (request?.id) {
        void queryClient.invalidateQueries({
          queryKey: requestKeys.requestById(request.id),
        });
      }
    });
  };

  return (
    <Controller
      control={methods.control}
      name="creatorId"
      render={({ field: { name, onChange, value } }) => {
        const selectedLabel =
          creators?.find((creator) => creator.id === value)?.textValue ??
          request?.creator.name;

        return (
          <Select
            aria-label="Change created by"
            name={name}
            onSelectionChange={(selectedValue) =>
              handleUpdate(Number(selectedValue), onChange)
            }
            selectedKey={value}
          >
            <Button
              className="flex items-center justify-center outline-none whitespace-nowrap"
              variant="unstyled"
            >
              <Badge size="small" variant="primary">
                {selectedLabel}
                <Badge.Icon>
                  <ChevronDownIcon />
                </Badge.Icon>
              </Badge>
            </Button>

            <SelectPopover
              className="flex w-[250px] flex-col overflow-hidden"
              placement="bottom start"
            >
              <Autocomplete filter={contains}>
                <SearchField
                  aria-label="Search customers"
                  autoFocus
                  className="shrink-0 px-sm pt-xs"
                >
                  <Input placeholder="Search..." />
                </SearchField>

                <SelectListBox
                  className="min-h-0"
                  renderEmptyState={() => (
                    <Text className="p-sm text-center text-text-tertiary">
                      No eligible customers found
                    </Text>
                  )}
                >
                  {creators?.map((creator) => (
                    <SelectListItem
                      id={creator.id}
                      key={creator.id}
                      textValue={creator.textValue}
                    >
                      <SelectListItemLabel className="block truncate">
                        {creator.textValue}
                      </SelectListItemLabel>
                      {creator.role && (
                        <SelectListItemDescription className="block w-full">
                          {creator.role}
                        </SelectListItemDescription>
                      )}
                    </SelectListItem>
                  ))}
                </SelectListBox>
              </Autocomplete>
            </SelectPopover>
          </Select>
        );
      }}
    />
  );
}
